import classNames from "classnames";
import pluralize from "pluralize";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { FinalReviewSummary } from "vl-api/vlSchemas";
import { formatNumber } from "helpers/utility/formatters";
import styles from "./style.module.scss";

interface FinalReviewStatsProps {
  summary: FinalReviewSummary;
}

/** Summary of the final review: labeled, changed and unlabeled images */
export default function FinalReviewStats({ summary }: FinalReviewStatsProps) {
  const labeled = summary.labeled_count ?? 0;
  const changed = summary.changed_count ?? 0;
  const unlabeled = summary.unlabeled_count ?? 0;
  const total = labeled + unlabeled;

  const tiles = [
    {
      key: "labeled",
      value: labeled,
      text: "Labeled",
      tooltip: "Images that will get a label when you apply",
    },
    {
      key: "changed",
      value: changed,
      text: "Changed by you",
      tooltip: "Images you reassigned to another label in this review",
      className: styles.changed,
    },
    {
      key: "unlabeled",
      value: unlabeled,
      text: "Unlabeled",
      tooltip: "Images you ignored. They will not get a label",
      className: styles.unlabeled,
    },
  ];

  return (
    <div className={styles.wrapper} data-testid="final-review-stats">
      <div className={styles.header}>
        <Tooltip title="Label Propagation finished. Review the labels below before applying them to your dataset">
          <span className={styles.title}>
            Propagation finished
            <InfoCircleOutlined className={styles.infoIcon} />
          </span>
        </Tooltip>
        <span className={styles.count}>
          {formatNumber(total)} {pluralize("image", total)}
        </span>
      </div>
      <div className={styles.tiles}>
        {tiles.map((tile) => (
          <Tooltip key={tile.key} title={tile.tooltip} mouseEnterDelay={0.4}>
            <div className={styles.tile} data-testid={`stat-${tile.key}`}>
              <span className={classNames(styles.value, tile.className)}>
                {formatNumber(tile.value)}
              </span>
              <span className={styles.text}>{tile.text}</span>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
